import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventoryStockLocationFilterParams,
  InventoryStockLocationRecord,
  InventoryStockLocationSummaryCounts,
  SaveInventoryStockLocationRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapInventoryStockLocation = (
  value: Record<string, unknown>,
): InventoryStockLocationRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: String(value.outlet_id || ''),
  name: String(value.name || ''),
  type: value.type as InventoryStockLocationRecord['type'],
  description: (value.description as string | null) ?? null,
  is_active: Boolean(value.is_active),
  is_default: Boolean(value.is_default),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const applyInventoryStockLocationFilters = <T>(
  query: T,
  filters: {
    search?: string;
    type?: InventoryStockLocationFilterParams['type'];
    status?: InventoryStockLocationFilterParams['status'];
  },
) => {
  let nextQuery = query as any;
  const trimmedSearch = filters.search?.trim();

  if (filters.type && filters.type !== 'all') {
    nextQuery = nextQuery.eq('type', filters.type);
  }

  if (filters.status && filters.status !== 'all') {
    nextQuery = nextQuery.eq('is_active', filters.status === 'active');
  }

  if (trimmedSearch) {
    nextQuery = nextQuery.or(
      `name.ilike.%${trimmedSearch}%,type.ilike.%${trimmedSearch}%,description.ilike.%${trimmedSearch}%`,
    );
  }

  return nextQuery as T;
};

export const fetchInventoryStockLocations = async ({
  companyId,
  outletId,
  search,
  type = 'all',
  status = 'all',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventoryStockLocationFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryStockLocationRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('inventory_stock_locations')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('name', { ascending: true })
      .range(from, to);

    query = applyInventoryStockLocationFilters(query, { search, type, status });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) =>
          mapInventoryStockLocation(item as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock locations',
      data: undefined,
    };
  }
};

export const fetchInventoryStockLocationSummary = async (
  companyId: number,
  outletId: string,
  search?: string,
  type: InventoryStockLocationFilterParams['type'] = 'all',
  status: InventoryStockLocationFilterParams['status'] = 'all',
): Promise<ApiResponse<InventoryStockLocationSummaryCounts>> => {
  try {
    const baseQuery = (activeStatus?: boolean) => {
      let query = supabase
        .from('inventory_stock_locations')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('outlet_id', outletId);

      query = applyInventoryStockLocationFilters(query, {
        search,
        type,
        status:
          activeStatus === undefined
            ? status
            : activeStatus
              ? 'active'
              : 'inactive',
      });

      return query;
    };

    const [totalResult, activeResult, inactiveResult, typesResult] =
      await Promise.all([
        baseQuery(),
        baseQuery(true),
        baseQuery(false),
        applyInventoryStockLocationFilters(
          supabase
            .from('inventory_stock_locations')
            .select('type')
            .eq('company_id', companyId)
            .eq('outlet_id', outletId),
          { search, type, status },
        ),
      ]);

    const errors = [
      totalResult.error,
      activeResult.error,
      inactiveResult.error,
      typesResult.error,
    ].filter(Boolean);

    if (errors.length) {
      return { success: false, error: errors[0]?.message, data: undefined };
    }

    const uniqueTypes = new Set(
      (typesResult.data || []).map((row: { type?: string | null }) => row.type),
    );

    return {
      success: true,
      data: {
        totalLocations: totalResult.count || 0,
        activeLocations: activeResult.count || 0,
        inactiveLocations: inactiveResult.count || 0,
        uniqueTypes: uniqueTypes.size,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock location summary',
      data: undefined,
    };
  }
};

export const saveInventoryStockLocation = async (
  input: SaveInventoryStockLocationRequest,
): Promise<ApiResponse<InventoryStockLocationRecord>> => {
  const payload = {
    company_id: input.companyId,
    outlet_id: input.outletId,
    name: input.name.trim(),
    type: input.type ?? 'store',
    description: input.description?.trim() || null,
    is_active: input.isActive ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_stock_locations')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('outlet_id', input.outletId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventoryStockLocation(data) };
    }

    const { data, error } = await supabase
      .from('inventory_stock_locations')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryStockLocation(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory stock location',
      data: undefined,
    };
  }
};

export const setDefaultInventoryStockLocation = async (
  locationId: string,
): Promise<ApiResponse<InventoryStockLocationRecord>> => {
  try {
    const { data, error } = await supabase.rpc('set_default_stock_location', {
      p_stock_location_id: locationId,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryStockLocation(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to set default stock location',
      data: undefined,
    };
  }
};

export const deleteInventoryStockLocation = async (
  companyId: number,
  outletId: string,
  locationId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('inventory_stock_locations')
      .delete()
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .eq('id', locationId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete inventory stock location',
      data: null,
    };
  }
};
